import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { billingService } from '@services';
import { accountActions, alertActions, pricingDrawerUiActions } from '@actions';
import { BILLING_TIME_MAP, BILLING_TYPE_MAP, PLUSPOINT_MIN_LOCATIONS, PLUSPOINT_PLAN } from '@constants/billing';
import { handleApiError } from '@helpers/api';
import { getActiveSubscriptions, getPricingPlans, needsSubscribe } from '@helpers/billing';
import { useTranslation } from '@hooks';

export const useSubscribeForm = ({ directoriesLocation = null } = {}) => {
    const account = useSelector(state => state.account?.account);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const currentLocations = account?.companies?.length ?? 0;
    const currentUsers = account?.users?.length ?? 0;

    const isUpdate = !needsSubscribe(account);
    const active = getActiveSubscriptions(account);
    const subscription = active.find(item => item.billingPlanName === BILLING_TYPE_MAP.PLUSPOINT) ?? active[0];

    const minLocations = Math.max(PLUSPOINT_MIN_LOCATIONS, currentLocations);
    const minUsers = Math.max(PLUSPOINT_PLAN.users, currentUsers);

    const [billingTimePlan, setBillingTimePlan] = useState(
        isUpdate ? (subscription?.productType ?? BILLING_TIME_MAP.ANNUAL) : BILLING_TIME_MAP.ANNUAL,
    );
    const [locations, setLocations] = useState(Math.max(minLocations, subscription?.locationsCount ?? 0));

    const paidUsers = isUpdate ? Math.max(0, account?.usersMaxCount ?? 0) : 0;
    const [users, setUsers] = useState(Math.max(minUsers, paidUsers));
    const [isLoading, setIsLoading] = useState(false);

    const pricing = useMemo(
        () => getPricingPlans(account?.locale, locations)[billingTimePlan],
        [account?.locale, locations, billingTimePlan],
    );

    const additionalUsers = Math.max(0, users - PLUSPOINT_PLAN.users);
    const planTotal = pricing.price[BILLING_TYPE_MAP.PLUSPOINT] * locations;
    const usersTotal = pricing.users * additionalUsers;
    const directoriesTotal = directoriesLocation ? pricing.directories : 0;

    const onSubscribe = async () => {
        setIsLoading(true);
        try {
            const data = await billingService.createSubscription({
                productName: BILLING_TYPE_MAP.PLUSPOINT,
                locationsCount: locations,
                usersCount: users,
                productType: billingTimePlan,
                currency: pricing.currency,
                directoriesLocationId: directoriesLocation?.id ?? null,
            });

            if (data.paymentUrl) {
                window.location = data.paymentUrl;
                return;
            }
            dispatch(alertActions.error(t('alertMessages.updateFail')));
        } catch (error) {
            handleApiError(error, 'alertMessages.updateFail');
        }
        setIsLoading(false);
    };

    const planPayload = {
        productName: BILLING_TYPE_MAP.PLUSPOINT,
        locationsCount: locations,
        usersCount: users,
        productType: billingTimePlan,
    };

    const onPreviewUpdate = () => {
        dispatch(pricingDrawerUiActions.setStep(1));
        dispatch(pricingDrawerUiActions.getUpdateSubscriptionData(planPayload));
    };

    const onUpdate = async () => {
        setIsLoading(true);
        try {
            await billingService.updateSubscription(planPayload);
            dispatch(accountActions.get());
            dispatch(pricingDrawerUiActions.setOpen({ open: false, type: null }));
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
        } catch (error) {
            handleApiError(error, 'alertMessages.updateFail');
        }
        setIsLoading(false);
    };

    return {
        billingTimePlan,
        setBillingTimePlan,
        locations,
        setLocations,
        minLocations,
        users,
        setUsers,
        minUsers,
        additionalUsers,
        pricing,
        planTotal,
        usersTotal,
        directoriesTotal,
        total: planTotal + usersTotal + directoriesTotal,
        directoriesLocation,
        isLoading,
        isUpdate,
        hasBillableSubscription: Boolean(subscription),
        onSubscribe,
        onPreviewUpdate,
        onUpdate,
    };
};
